import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Filter chips — the operational way to slice a queue.
 * Press-down on click; no glow; soft surface on active; never a "pill".
 */
export interface ChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean
  count?: number
}

export const Chip = React.forwardRef<HTMLButtonElement, ChipProps>(
  ({ className, active, count, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-pressed={active}
      className={cn(
        'group inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium',
        'transition-all duration-fluid ease-fluid motion-reduce:transition-none',
        'active:translate-y-px motion-reduce:active:translate-y-0',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        active
          ? 'border-navy-300 bg-navy-100 text-navy-800 shadow-soft'
          : 'border-slate-200 bg-white text-slate-600 hover:border-navy-200 hover:bg-navy-50 hover:text-navy-800',
        className,
      )}
      {...props}
    >
      <span>{children}</span>
      {count != null && (
        <span
          className={cn(
            'rounded px-1 text-2xs tabular-nums leading-4',
            active ? 'bg-navy-200 text-navy-900' : 'bg-slate-100 text-slate-600 group-hover:bg-navy-100',
          )}
        >
          {count}
        </span>
      )}
    </button>
  ),
)
Chip.displayName = 'Chip'

export function ChipGroup({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('flex flex-wrap items-center gap-1.5', className)}>{children}</div>
}
